import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Sparkles, ShieldCheck, Layers, Globe } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/api";
import LanguageToggle from "@/components/LanguageToggle";

const SANS = '"Geist", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full grid lg:grid-cols-[1.1fr_1fr] bg-white"
      style={{ fontFamily: SANS }}
    >
      {/* ============================================================
          LEFT — Hero (hidden on mobile)
          ============================================================ */}
      <aside
        className="relative hidden lg:flex flex-col justify-between overflow-hidden px-16 py-12 bg-white"
        data-testid="login-hero"
      >
        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(rgba(15,23,42,0.07) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
            maskImage:
              "radial-gradient(ellipse at 30% 50%, black 35%, transparent 80%)",
          }}
        />

        {/* Two soft emerald orbs for depth */}
        <div
          className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(circle, #34d399 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-40 -right-32 h-[460px] w-[460px] rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(circle, #10b981 0%, transparent 70%)" }}
        />

        {/* ---------- TOP : status pill ---------- */}
        <div className="relative z-10 flex items-center justify-between">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-medium text-slate-700 shadow-sm"
            data-testid="status-pill"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            {t("auth.status_operational")}
          </div>
        </div>

        {/* ---------- MIDDLE : LOGO (hero) + headline ---------- */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-16 max-w-2xl">
          {/* BIG featured logo */}
          <div className="mb-12" data-testid="hero-logo">
            <div className="flex items-center gap-5">
              <div className="relative">
                {/* Glow halo — slow pulse */}
                <div className="absolute inset-0 bg-emerald-500 rounded-3xl blur-2xl halo-pulse" />
                {/* Logo tile */}
                <div className="relative h-20 w-20 rounded-3xl bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-700 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                  <Sparkles className="h-10 w-10 text-white" strokeWidth={2.2} />
                  {/* Inner highlight */}
                  <div className="absolute top-1.5 left-1.5 right-1.5 h-6 rounded-2xl bg-white/20 blur-sm" />
                </div>
              </div>
              <div className="leading-none">
                <div
                  className="text-[40px] font-semibold tracking-[-0.03em] text-slate-900"
                  style={{ fontFamily: SANS }}
                >
                  Nexaforge<span className="text-emerald-600">.</span>
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-slate-500 font-medium">
                  Prospect Solutions
                </div>
              </div>
            </div>
          </div>

          {/* Headline — clean modern sans, tight tracking */}
          <h1
            className="text-[44px] xl:text-[52px] leading-[1.05] font-semibold tracking-[-0.035em] text-slate-900"
            data-testid="hero-headline"
            style={{ fontFamily: SANS }}
          >
            {t("auth.split_headline_pre")}{" "}
            <br className="hidden xl:block" />
            <span className="text-emerald-600">{t("auth.split_headline_em")}</span>
          </h1>

          <p
            className="mt-6 text-[16px] leading-[1.6] text-slate-500 max-w-lg font-normal"
            data-testid="hero-sub"
            style={{ fontFamily: SANS }}
          >
            {t("auth.split_sub")}
          </p>

          {/* Feature rows — clean, fintech style, stagger 80ms entrance */}
          <div className="mt-10 space-y-3 max-w-md">
            <FeatureRow Icon={Sparkles} label={t("auth.split_feat1")} delay={0} />
            <FeatureRow Icon={Layers} label={t("auth.split_feat2")} delay={80} />
            <FeatureRow Icon={Globe} label={t("auth.split_feat3")} delay={160} />
          </div>
        </div>

        {/* ---------- BOTTOM : minimal stats ---------- */}
        <div className="relative z-10 grid grid-cols-3 gap-8 max-w-xl border-t border-slate-200 pt-7">
          <Stat value={t("auth.split_stat1_value")} label={t("auth.split_stat1_label")} />
          <Stat value={t("auth.split_stat2_value")} label={t("auth.split_stat2_label")} />
          <Stat value={t("auth.split_stat3_value")} label={t("auth.split_stat3_label")} />
        </div>
      </aside>

      {/* ============================================================
          RIGHT — Form
          ============================================================ */}
      <section
        className="relative flex flex-col px-6 sm:px-12 lg:px-16 py-8 lg:py-12"
        style={{
          background: "linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%)",
          fontFamily: SANS,
        }}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3" data-testid="mobile-logo">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500 rounded-xl blur-md opacity-40" />
              <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-700 flex items-center justify-center shadow-md shadow-emerald-500/30">
                <Sparkles className="h-5 w-5 text-white" strokeWidth={2.4} />
              </div>
            </div>
            <div className="leading-none">
              <div className="text-[18px] font-semibold tracking-[-0.02em] text-slate-900">
                Nexaforge<span className="text-emerald-600">.</span>
              </div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.28em] text-slate-500 font-medium">
                Prospect Solutions
              </div>
            </div>
          </div>
          <div className="hidden lg:block" />
          <LanguageToggle />
        </div>

        {/* Form center */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[380px] py-10">
            {/* Card */}
            <div
              className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-8px_rgba(15,23,42,0.10)] p-8"
              data-testid="login-card"
            >
              <div className="mb-7">
                <h2
                  className="text-[26px] font-semibold tracking-[-0.025em] text-slate-900 leading-tight"
                  data-testid="login-title"
                >
                  {t("auth.welcome_back")}
                </h2>
                <p className="mt-2 text-[14px] text-slate-500 font-normal">
                  {t("auth.login_hint")}
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[12px] font-medium text-slate-700">
                    {t("auth.email")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.email_placeholder", "vous@entreprise.com")}
                    className="h-11 bg-slate-50/60 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-emerald-500/25 focus-visible:border-emerald-500/70 focus-visible:bg-white transition-all"
                    data-testid="login-email"
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-[12px] font-medium text-slate-700">
                    {t("auth.password")}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-11 bg-slate-50/60 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-emerald-500/25 focus-visible:border-emerald-500/70 focus-visible:bg-white transition-all"
                    data-testid="login-password"
                    autoComplete="current-password"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 text-[14px] font-medium bg-slate-900 hover:bg-slate-800 text-white border-0 shadow-sm transition-all group mt-2"
                  data-testid="login-submit"
                >
                  {loading ? (
                    t("auth.signing_in")
                  ) : (
                    <>
                      {t("auth.sign_in")}
                      <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Trust line below card */}
            <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-slate-400">
              <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
              JWT · Multi-tenant · GDPR
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <div>{t("auth.footer_legal")}</div>
          <div className="hidden sm:flex items-center gap-4">
            <a href="#" className="hover:text-slate-600 transition-colors">{t("auth.terms")}</a>
            <a href="#" className="hover:text-slate-600 transition-colors">{t("auth.privacy")}</a>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ============================================================
   Helpers
   ============================================================ */

function Stat({ value, label }) {
  return (
    <div className="text-left">
      <div
        className="text-[28px] font-semibold tracking-[-0.03em] text-slate-900 leading-none"
        style={{ fontFamily: SANS }}
      >
        {value}
      </div>
      <div className="mt-2 text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">
        {label}
      </div>
    </div>
  );
}

function FeatureRow({ Icon, label, delay = 0 }) {
  return (
    <div
      className="flex items-center gap-3 text-[14px] text-slate-700 fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="h-8 w-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
        <Icon className="h-3.5 w-3.5 text-emerald-700" strokeWidth={2.2} />
      </div>
      <span className="font-medium tracking-[-0.005em]">{label}</span>
    </div>
  );
}
